import { Box } from '@mui/material';
import { RefObject, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { ElementSize, useElementSize } from '~/hooks/useElementSize';
import { histogramHintEnabledState } from '~/state/atoms/toolsAtoms';
import { READOUT, READOUT_FONT } from '../chrome';
import {
    MIN_STRIP_WIDTH_PX,
    STRIP_BLOCK_HEIGHT_PX,
} from './IntensityHistogramStrip';
import { ScaleBar } from './tileViewModel';
import WindowHistogram, {
    HISTOGRAM_CAPTION_MAX_WIDTH_PX,
    WindowHistogramImage,
} from './WindowHistogram';

/** Distance of the corner stack from the tile's left and bottom edges, in CSS px. */
const CORNER_INSET_PX = 6;
/** Space between two items of the corner stack, in CSS px. */
const STACK_GAP_PX = 4;
/** Space between the histogram strips of two images, in CSS px. */
const STRIP_GAP_PX = 12;
/** Space between a strip's bar and the name beside it, in CSS px. */
const CAPTION_GAP_PX = 4;
/** The widest a histogram strip is drawn on a tile, in CSS px. */
const MAX_TILE_STRIP_WIDTH_PX = 160;
/**
 * Space kept between the corner and the middle of the tile edge it runs
 * along, where that edge's orientation letter sits, in CSS px: half the
 * widest letter chip and a gap.
 */
const EDGE_LETTER_CLEARANCE_PX = 16;
/**
 * Width kept free along the bottom edge, at the right, for the readouts of
 * the point under the pointer, in CSS px: the widest of them, the patient
 * coordinates line, measures about 190 px.
 */
const POINTER_READOUT_RESERVE_PX = 220;
/** Height of the scale bar's label line, in CSS px. */
const SCALE_BAR_LABEL_HEIGHT_PX = 13;
/** Height of the scale bar's tick, in CSS px. */
const SCALE_BAR_TICK_HEIGHT_PX = 5;
/** Height of the scale bar, its label and the 2 px between included, in CSS px. */
const SCALE_BAR_HEIGHT_PX =
    SCALE_BAR_LABEL_HEIGHT_PX + 2 + SCALE_BAR_TICK_HEIGHT_PX;
/** Height kept above the corner for the one-row legend a host places there, in CSS px. */
const CORNER_LEGEND_ALLOWANCE_PX = 20;

/**
 * The width the window histogram strips are drawn at in a tile of `tile`
 * size, their names left out, or undefined when they do not fit. They must
 * never cover an orientation letter or a pointer readout: along the bottom
 * edge the strips and the column of their names stop clear of its middle
 * and of the room the pointer readouts take at its right end, and up the
 * left edge the whole corner (the histograms, the scale bar above them
 * while it is shown, and room for a legend above that) stays clear of its
 * middle. Within that they narrow with the tile, down to the narrowest a
 * strip can be read at.
 */
export function tileHistogramWidthPx(
    tile: ElementSize,
    images: ReadonlyArray<Pick<WindowHistogramImage, 'pairLabel'>>,
    scaleBarShown: boolean
): number | undefined {
    if (images.length === 0) {
        return undefined;
    }
    const captionColumnPx = images.some((image) => image.pairLabel)
        ? HISTOGRAM_CAPTION_MAX_WIDTH_PX + CAPTION_GAP_PX
        : 0;
    const widthPx = Math.min(
        MAX_TILE_STRIP_WIDTH_PX,
        Math.floor(
            tile.widthPx / 2 -
                CORNER_INSET_PX -
                EDGE_LETTER_CLEARANCE_PX -
                captionColumnPx
        ),
        Math.floor(
            tile.widthPx -
                2 * CORNER_INSET_PX -
                POINTER_READOUT_RESERVE_PX -
                STACK_GAP_PX -
                captionColumnPx
        )
    );
    if (widthPx < MIN_STRIP_WIDTH_PX) {
        return undefined;
    }
    const histogramsHeightPx =
        images.length * STRIP_BLOCK_HEIGHT_PX +
        (images.length - 1) * STRIP_GAP_PX;
    const cornerTopPx =
        CORNER_INSET_PX +
        histogramsHeightPx +
        (scaleBarShown ? STACK_GAP_PX + SCALE_BAR_HEIGHT_PX : 0) +
        STACK_GAP_PX +
        CORNER_LEGEND_ALLOWANCE_PX;
    if (cornerTopPx > tile.heightPx / 2 - EDGE_LETTER_CLEARANCE_PX) {
        return undefined;
    }
    return widthPx;
}

interface TileCornerProps {
    /** The scale bar, while it is shown. */
    readonly scaleBar: ScaleBar | undefined;
    /** The images whose window histograms the corner draws, while the histogram toggle is on. */
    readonly histograms: ReadonlyArray<WindowHistogramImage>;
    /** Receives the corner's stack, whose height a legend placed above it keeps clear of. */
    readonly stackRef: RefObject<HTMLDivElement>;
}

/**
 * The bottom-left corner of a tile: the window histogram of each image the
 * tile draws on the bottom edge, while the histogram toggle is on and the
 * tile has room for them, with the scale bar above. Laid over the whole
 * view, so it sizes the histograms by the view; only the histograms take
 * the pointer, for their distribution details on hover.
 */
export default function TileCorner({
    scaleBar,
    histograms,
    stackRef,
}: TileCornerProps) {
    const histogramShown = useRecoilValue(histogramHintEnabledState);
    const viewRef = useRef<HTMLDivElement>(null);
    const view = useElementSize(viewRef);
    const histogramWidthPx =
        histogramShown && view
            ? tileHistogramWidthPx(view, histograms, scaleBar !== undefined)
            : undefined;
    return (
        <Box
            ref={viewRef}
            sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        >
            <Box
                ref={stackRef}
                data-testid="tile-corner"
                sx={{
                    position: 'absolute',
                    left: CORNER_INSET_PX,
                    bottom: CORNER_INSET_PX,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: `${STACK_GAP_PX}px`,
                }}
            >
                {scaleBar ? (
                    <Box
                        data-testid="scale-bar"
                        aria-hidden
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            fontFamily: READOUT_FONT,
                            fontSize: 11,
                            color: READOUT,
                        }}
                    >
                        <Box
                            sx={{
                                mb: 0.25,
                                lineHeight: `${SCALE_BAR_LABEL_HEIGHT_PX}px`,
                            }}
                        >
                            {scaleBar.label}
                        </Box>
                        <Box
                            sx={{
                                width: `${scaleBar.lengthPx}px`,
                                height: SCALE_BAR_TICK_HEIGHT_PX,
                                borderLeft: 1,
                                borderRight: 1,
                                borderBottom: 1,
                                borderColor: READOUT,
                            }}
                        />
                    </Box>
                ) : null}
                {histogramWidthPx !== undefined ? (
                    <Box
                        data-testid="tile-histograms"
                        sx={{
                            display: 'grid',
                            // The names of a pair share one column, so their
                            // bars line up.
                            gridTemplateColumns: histograms.some(
                                (image) => image.pairLabel
                            )
                                ? `auto ${histogramWidthPx}px`
                                : `${histogramWidthPx}px`,
                            columnGap: `${CAPTION_GAP_PX}px`,
                            rowGap: `${STRIP_GAP_PX}px`,
                            alignItems: 'start',
                            pointerEvents: 'auto',
                        }}
                    >
                        {histograms.map((image) => (
                            <WindowHistogram
                                key={image.seriesInstanceUid}
                                {...image}
                                widthPx={histogramWidthPx}
                            />
                        ))}
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
}
